/**
 * IOT-HAL-1: Syntra adapter — DeviceContract implementation for Syntra hardware.
 *
 * Syntra is Kairos's own hardware line. This adapter has full control surface:
 * OTA config push, attestation, rollback, onboard alerting, full telemetry.
 *
 * AWS IoT Core wiring is NOT here — SyntraAdapter accepts an injected
 * SyntraCloudClient at construction. The real client lands in IOT-CLOUD-1.
 * Today this adapter is complete and tested against a fake client.
 *
 * Foundation Purity (BINDING): no tenant-specific logic in this file.
 * Tenant-specific thresholds live in config/policy layers, not here.
 */

import { createId } from '../../utils/createId.js';

import { normalizeTimestamp, timestampQualityFlag } from '../../normalization/normalizeTimestamp.js';
import { normalizeEventType } from '../../normalization/normalizeEventType.js';
import { canonicalUnit } from '../../normalization/normalizeUnits.js';

// ── Capability matrix imports ─────────────────────────────────────────────────

import KTrackColdSingle  from './capabilities/K-Track-Cold-Single.json'
import KTrackColdPlus    from './capabilities/K-Track-Cold-Plus.json'  
import KTrackAsset       from './capabilities/K-Track-Asset.json'      
import KFleetVehicle     from './capabilities/K-Fleet-Vehicle.json'    
import KEdgeGateway      from './capabilities/K-Edge-Gateway.json'     
import KSenseAmbient     from './capabilities/K-Sense-Ambient.json'    

/** Map of Syntra model string → CapabilityMatrix declaration */
const SYNTRA_CAPABILITY_MAP = {
  'K-Track-Cold-Single': KTrackColdSingle,
  'K-Track-Cold-Plus':   KTrackColdPlus,
  'K-Track-Asset':       KTrackAsset,
  'K-Fleet-Vehicle':     KFleetVehicle,
  'K-Edge-Gateway':      KEdgeGateway,
  'K-Sense-Ambient':     KSenseAmbient,
};

/** Syntra→canonical event type mapping */
const SYNTRA_EVENT_MAP = {
  'TEMP_BREACH':           'threshold_breach',
  'TEMP_RECOVERY':         'threshold_recovery',
  'HUMIDITY_BREACH':       'threshold_breach',
  'HUMIDITY_RECOVERY':     'threshold_recovery',
  'CO2_BREACH':            'threshold_breach',
  'PRESSURE_BREACH':       'threshold_breach',
  'SHOCK_DETECTED':        'shock_event',
  'DOOR_OPENED':           'door_open',
  'DOOR_CLOSED':           'door_close',
  'GEOFENCE_ENTERED':      'geofence_enter',
  'GEOFENCE_EXITED':       'geofence_exit',
  'BATT_LOW':              'low_battery',
  'CELL_LOST':             'connectivity_lost',
  'CELL_RESTORED':         'connectivity_restored',
  'FIRMWARE_UPDATED':      'firmware_updated',
  'TAMPER_DETECTED':       'tamper_detected',
  'SELF_TEST_FAILED':      'self_test_failed',
  'CONFIG_PUSHED':         'config_pushed',
  'CONFIG_APPLIED':        'config_applied',
  'CONFIG_FAILED':         'config_failed',
  'LIFECYCLE_CHANGED':     'lifecycle_transition',
  'OVERSPEED':             'threshold_breach',
  'IDLE_ALERT':            'threshold_breach',
};

// ── SyntraAdapter ─────────────────────────────────────────────────────────────

export class SyntraAdapter {
  /**
   * @param {import('../../contracts/DeviceContract.ts').DeviceRecord} device
   * @param {object} cloudClient  injected SyntraCloudClient (real in IOT-CLOUD-1, fake in tests)
   */
  constructor(device, cloudClient) {
    if (!SYNTRA_CAPABILITY_MAP[device.model]) {
      throw new Error(
        `SyntraAdapter: unknown Syntra model "${device.model}". ` +
        `Known models: ${Object.keys(SYNTRA_CAPABILITY_MAP).join(', ')}`
      );
    }
    this._device = device;
    this._client = cloudClient;
    this._capabilities = SYNTRA_CAPABILITY_MAP[device.model];
    this._lifecycleState = device.lifecycleState || 'provisioned';
    this._telemetrySubscribers = [];
    this._eventSubscribers = [];
  }

  // ── Identity ────────────────────────────────────────────────────────────────

  getDeviceId()     { return this._device.id; }
  getVendor()       { return 'syntra'; }
  getModel()        { return this._device.model; }
  getCapabilities() { return this._capabilities; }

  // ── Configuration ───────────────────────────────────────────────────────────

  async pushConfig(delta, actor) {
    const receiptId = createId('rcpt');
    const pushedAt  = new Date().toISOString();

    // Pre-validate keys against capability matrix before touching the cloud client.
    // HalService also validates, but adapter is the contract boundary.
    const failedKeys = [];
    const validChanges = {};
    for (const [key, value] of Object.entries(delta.changes)) {
      const cap = this._capabilities.configKeys[key];
      if (!cap) {
        failedKeys.push({ key, reason: 'unknown config key' });
      } else if (!cap.pushable) {
        failedKeys.push({ key, reason: 'not pushable' });
      } else {
        validChanges[key] = value;
      }
    }

    if (failedKeys.length > 0 && Object.keys(validChanges).length === 0) {
      return {
        receiptId,
        deviceId:          this._device.id,
        status:            'failed',
        pushedAt,
        failedKeys,
        rollbackAvailable: this._capabilities.rollbackSupported,
      };
    }

    if (Object.keys(validChanges).length === 0) {
      return {
        receiptId,
        deviceId:          this._device.id,
        status:            'failed',
        pushedAt,
        failedKeys,
        rollbackAvailable: this._capabilities.rollbackSupported,
      };
    }

    const result = await this._client.pushShadowDelta(this._device.id, {
      state:    { desired: validChanges },
      metadata: { actor: actor.uid || actor.agent, reason: delta.reason },
    });

    const status = !result.accepted
      ? 'failed'
      : failedKeys.length > 0
        ? 'partially_applied'
        : 'pending';

    return {
      receiptId,
      deviceId:          this._device.id,
      status,
      pushedAt,
      vendorAckAt:       result.accepted ? new Date().toISOString() : undefined,
      failedKeys:        [
        ...failedKeys,
        ...(result.rejectedKeys?.map((k) => ({ key: k, reason: result.rejectedReason || 'rejected' })) || []),
      ],
      rollbackAvailable: this._capabilities.rollbackSupported,
    };
  }

  async getCurrentConfig() {
    const shadow = await this._client.getShadow(this._device.id);
    return {
      schemaVersion: 1,
      tenantId: this._device.tenantId,
      configValues: shadow.state?.reported || {},
      configVersion: shadow.version || 0,
      appliedAt: shadow.metadata?.reportedAt || new Date().toISOString(),
    };
  }

  async getPendingConfig() {
    const shadow = await this._client.getShadow(this._device.id);
    if (!shadow.state?.desired) return null;
    return {
      schemaVersion: 1,
      tenantId: this._device.tenantId,
      configValues: shadow.state.desired,
      configVersion: shadow.version || 0,
      appliedAt: shadow.metadata?.desiredAt || new Date().toISOString(),
    };
  }

  async rollbackConfig(toVersion, actor) {
    const receiptId = createId('rcpt');
    const result = await this._client.rollbackShadow(this._device.id, toVersion, {
      actor: actor.uid || actor.agent,
      reason: actor.reason,
    });
    return {
      receiptId,
      deviceId: this._device.id,
      status: result.accepted ? 'pending' : 'failed',
      pushedAt: new Date().toISOString(),
      rollbackAvailable: this._capabilities.rollbackSupported,
    };
  }

  // ── Telemetry ────────────────────────────────────────────────────────────────

  async getTelemetry(window, sensorTypes) {
    const raw = await this._client.queryTelemetry(this._device.id, window, sensorTypes);
    return raw.map((r) => this._normalizeReading(r));
  }

  subscribeToTelemetry(handler) {
    this._telemetrySubscribers.push(handler);
    const unsubscribeFn = this._client.subscribeTelemetry(this._device.id, (raw) => {
      const normalized = this._normalizeReading(raw);
      handler(normalized);
    });
    return {
      unsubscribe: () => {
        this._telemetrySubscribers = this._telemetrySubscribers.filter((h) => h !== handler);
        if (typeof unsubscribeFn === 'function') unsubscribeFn();
      },
    };
  }

  // ── Health ──────────────────────────────────────────────────────────────────

  async getDeviceHealth() {
    const h = await this._client.getDeviceHealth(this._device.id);
    return {
      deviceId: this._device.id,
      lastSeenAt: normalizeTimestamp(h.lastSeenAt) || new Date().toISOString(),
      batterySocPct:            h.batterySocPct,
      batteryVoltageV:          h.batteryVoltageV,
      cellSignalDbm:            h.cellSignalDbm,
      satelliteSignalDbm:       h.satelliteSignalDbm,
      firmwareVersion:          h.firmwareVersion || 'unknown',
      firmwareUpdateAvailable:  h.firmwareUpdateAvailable ?? false,
      uptimeSec:                h.uptimeSec,
      freeStorageBytes:         h.freeStorageBytes,
      lastSelfTestPassed:       h.lastSelfTestPassed ?? true,
      connectivityStatus:       h.connectivityStatus || 'online',
      estimatedBatteryDaysRemaining: h.estimatedBatteryDaysRemaining,
    };
  }

  // ── Events ──────────────────────────────────────────────────────────────────

  subscribeToEvents(handler) {
    this._eventSubscribers.push(handler);
    const unsubscribeFn = this._client.subscribeEvents(this._device.id, (raw) => {
      const canonicalType = normalizeEventType(raw.eventType, SYNTRA_EVENT_MAP);
      if (!canonicalType) return; // drop unmapped vendor events
      const event = {
        eventId:   raw.eventId || createId('evt'),
        deviceId:  this._device.id,
        tenantId:  this._device.tenantId,
        eventType: canonicalType,
        severity:  raw.severity || 'info',
        payload:   raw.payload || {},
        timestamp: normalizeTimestamp(raw.timestamp) || new Date().toISOString(),
        vendor:    'syntra',
      };
      handler(event);
    });
    return {
      unsubscribe: () => {
        this._eventSubscribers = this._eventSubscribers.filter((h) => h !== handler);
        if (typeof unsubscribeFn === 'function') unsubscribeFn();
      },
    };
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  getLifecycleState() { return this._lifecycleState; }

  async setLifecycleState(state, actor) {
    await this._client.updateLifecycleState(this._device.id, state, {
      actor: actor.uid || actor.agent,
      reason: actor.reason,
    });
    this._lifecycleState = state;
  }

  // ── Internal helpers ─────────────────────────────────────────────────────────

  _normalizeReading(raw) {
    const ts = normalizeTimestamp(raw.timestamp);
    const qualityFlag = !ts
      ? 'invalid'
      : raw.qualityFlag || timestampQualityFlag(ts);
    return {
      deviceId:          this._device.id,
      tenantId:          this._device.tenantId,
      sensorType:        raw.sensorType,
      value:             raw.value,
      unit:              raw.unit || canonicalUnit(raw.sensorType),
      timestamp:         ts || new Date().toISOString(),
      vendor:            'syntra',
      vendorRawPayload:  raw._raw,
      qualityFlag,
    };
  }
}

// ── Factory ───────────────────────────────────────────────────────────────────

/**
 * Factory function for HalService.registerAdapter('syntra', SyntraAdapterFactory).
 * cloudClient is injected by the factory closure — real in prod, fake in tests.
 *
 * @param {object} cloudClient
 * @returns {import('../../contracts/DeviceContract.ts').AdapterFactory}
 */
export function createSyntraAdapterFactory(cloudClient) {
  return (device) => new SyntraAdapter(device, cloudClient);
}

/**
 * Parse a raw Syntra inbound webhook payload into a NormalizedReading array.
 * Used by the iotIngest Cloud Function (Day 3).
 *
 * @param {string} deviceId
 * @param {string} tenantId
 * @param {object} rawPayload
 * @returns {import('../../contracts/DeviceContract.ts').NormalizedReading[]}
 */
export function parseSyntraInbound(deviceId, tenantId, rawPayload) {
  if (!rawPayload?.readings) return [];
  return rawPayload.readings.map((r) => {
    const ts = normalizeTimestamp(r.ts || r.timestamp);
    return {
      deviceId,
      tenantId,
      sensorType: r.type,
      value:      r.v ?? r.value,
      unit:       r.unit || canonicalUnit(r.type),
      timestamp:  ts || new Date().toISOString(),
      vendor:     'syntra',
      vendorRawPayload: r,
      qualityFlag: ts ? timestampQualityFlag(ts) : 'invalid',
    };
  });
}
